import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SeoHead } from '../components/SeoHead';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Play,
  ChevronRight,
  Calendar,
  MapPin,
  Tv,
  Video,
  Sparkles,
  TrendingUp,
  Award,
  Target,
  Zap,
  Users,
  Radio,
  Star,
  ChevronLeft,
  Loader2,
} from 'lucide-react';
import { mockAthletes } from '../mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hero background
const HERO_VIDEO = 'https://customer-assets.emergentagent.com/job_f4179c34-6cc2-42f7-b341-41bdd7c10fda/artifacts/lf3t887j_VIDEO-2026-01-01-12-18-16.mp4';

// Rooam brand colors
const BRAND_COLORS = {
  blue: '#00d4ff',
  blueLight: '#33ddff',
  blueDark: '#00a8cc',
  red: '#ff0000',
  black: '#0a0a0a',
};

// Sport accent colors matching Rooam brand
const getSportStyle = (sport) => {
  const sportLower = (sport || '').toLowerCase();
  if (sportLower.includes('basketball')) return { accent: BRAND_COLORS.red, bg: 'bg-[#ff0000]' };
  if (sportLower.includes('soccer') || sportLower.includes('futbol')) return { accent: '#30D158', bg: 'bg-[#30D158]' };
  if (sportLower.includes('volleyball')) return { accent: BRAND_COLORS.blue, bg: 'bg-[#00d4ff]' };
  if (sportLower.includes('football')) return { accent: '#AF52DE', bg: 'bg-[#AF52DE]' };
  return { accent: BRAND_COLORS.blue, bg: 'bg-[#00d4ff]' };
};

// Animated counter component
const AnimatedCounter = ({ end, label, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    
    const duration = 2000;
    const steps = 60;
    const increment = end / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [isVisible, end]);

  return (
    <div ref={ref} className="text-center">
      <div 
        className="text-3xl sm:text-4xl md:text-5xl font-black text-white tabular-nums"
        style={{ fontFamily: 'Oswald, sans-serif' }}
      >
        {count.toLocaleString()}{suffix}
      </div>
      <div className="text-xs sm:text-sm text-zinc-500 uppercase tracking-widest mt-1">{label}</div>
    </div>
  );
};

// Live ticker item
const LiveTickerItem = ({ game }) => {
  const isLive = game.status === 'live' || game.status === 'halftime';
  
  // Use team names or sport-based fallback
  const homeTeam = game.home_team_name || game.home_name || 'Team A';
  const awayTeam = game.away_team_name || game.away_name || 'Team B';
  
  return (
    <Link 
      to={`/game-live/${game.game_id || game.id}`}
      className="flex-shrink-0 flex items-center gap-4 px-5 py-3 bg-[#141414] border border-white/10 rounded-xl hover:border-[#00d4ff]/50 transition-all group"
    >
      {isLive && (
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 bg-[#E5FF00] rounded-full animate-pulse" />
          <span className="text-[#E5FF00] text-xs font-bold">LIVE</span>
        </div>
      )}
      <div className="text-sm">
        <span className="font-semibold text-white">{homeTeam}</span>
        <span className="text-zinc-500 mx-2">vs</span>
        <span className="font-semibold text-white">{awayTeam}</span>
      </div>
      {isLive && (
        <span 
          className="text-lg font-bold text-[#00d4ff] tabular-nums"
          style={{ fontFamily: 'Oswald, sans-serif' }}
        >
          {game.score?.home ?? 0}-{game.score?.away ?? 0}
        </span>
      )}
      <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-[#00d4ff] transition-colors" />
    </Link>
  );
};

// Featured Event Card - Panoramic style
const FeaturedEventCard = ({ event, isLarge = false }) => {
  const sportStyle = getSportStyle(event.sport);
  const hasLive = event.status === 'live' || event.status === 'pool_play';
  
  return (
    <Link 
      to={event.is_multi_court ? `/event/${event.id}/courts` : `/event/${event.id}`}
      className={`group relative block overflow-hidden rounded-2xl border border-white/10 hover:border-white/20 transition-all duration-300 ${isLarge ? 'aspect-[21/9]' : 'aspect-[16/9]'}`}
      data-testid={`event-card-${event.id}`}
    >
      {/* Background */}
      <div className="absolute inset-0 bg-black">
        {event.cover_image ? (
          <img
            src={event.cover_image}
            alt={event.name || ''}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <img src={event.logo_url || (event.id === 'efb99264-287c-4957-857e-59fac86cda01' ? '/ek-cali-classic-logo.png' : '/rooam-logo.png')} alt="" className="w-32 h-32 md:w-40 md:h-40 object-contain opacity-30" />
          </div>
        )}
        {/* Dim overlay so the text bar at the bottom stays readable when a cover is used */}
        {event.cover_image && (
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        )}
      </div>
      
      {/* Badges */}
      <div className="absolute top-4 left-4 flex items-center gap-2">
        {hasLive && (
          <div className="flex items-center gap-1.5 bg-black/60 backdrop-blur-md px-2.5 py-1 rounded-full">
            <span className="w-2 h-2 bg-[#E5FF00] rounded-full animate-pulse" />
            <span className="text-[#E5FF00] text-xs font-bold">LIVE</span>
          </div>
        )}
        {event.is_featured && (
          <div className="flex items-center gap-1.5 bg-[#E5FF00] text-black px-2.5 py-1 rounded-full text-xs font-bold">
            <Star className="w-3 h-3" fill="currentColor" />
            FEATURED
          </div>
        )}
      </div>
      
      {/* Sport Badge */}
      <div className="absolute top-4 right-4">
        <span className={`${sportStyle.bg} text-white text-[10px] font-bold px-2.5 py-1 rounded uppercase tracking-wider`}>
          {(event.sport || 'Sports').split(',')[0]}
        </span>
      </div>
      
      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
        <p className="text-zinc-500 text-xs uppercase tracking-widest mb-1">
          {event.organizer || 'ROOAM SPORTS NETWORK'}
        </p>
        <h3 
          className={`font-black text-white uppercase tracking-tight leading-tight group-hover:text-[#00d4ff] transition-colors ${isLarge ? 'text-2xl md:text-3xl lg:text-4xl' : 'text-xl md:text-2xl'}`}
          style={{ fontFamily: 'Oswald, sans-serif' }}
        >
          {event.name}
        </h3>
        
        <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-zinc-400">
          {event.location && (
            <span className="flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5 text-[#00d4ff]" />
              {event.location}
            </span>
          )}
          {event.date && (
            <span className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5 text-[#00d4ff]" />
              {new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          )}
        </div>
      </div>
      
      {/* Play overlay */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="w-16 h-16 rounded-full bg-[#00d4ff] flex items-center justify-center shadow-xl shadow-[#00d4ff]/30">
          <Play className="w-7 h-7 text-white ml-1" fill="currentColor" />
        </div>
      </div>
    </Link>
  );
};

// Athlete Card - Command Center style
const AthleteCard = ({ athlete }) => (
  <Link 
    to={`/athlete/${athlete.id}`}
    className="group block bg-[#141414] border border-white/10 rounded-2xl overflow-hidden hover:border-[#00d4ff]/50 transition-all"
    data-testid={`athlete-card-${athlete.id}`}
  >
    <div className="p-5">
      <div className="flex items-start gap-4">
        <img
          src={athlete.avatar}
          alt={athlete.name}
          className="w-14 h-14 rounded-full border-2 border-[#00d4ff] object-cover"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 
              className="font-bold text-white truncate group-hover:text-[#00d4ff] transition-colors"
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              {athlete.name}
            </h3>
            {athlete.verified && (
              <Badge className="bg-[#00d4ff]/20 text-[#00d4ff] border-0 px-1.5 py-0">
                <Award className="h-3 w-3" />
              </Badge>
            )}
          </div>
          <p className="text-sm text-zinc-400">{athlete.position}</p>
          <p className="text-xs text-zinc-600">{athlete.team}</p>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-white/10">
        <div className="text-center">
          <div className="text-lg font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
            {athlete.games}
          </div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Games</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
            {athlete.highlights}
          </div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Highlights</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-[#00d4ff]" style={{ fontFamily: 'Oswald, sans-serif' }}>
            {Object.values(athlete.stats)[0]}
          </div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
            {Object.keys(athlete.stats)[0]}
          </div>
        </div>
      </div>
    </div>
  </Link>
);

// Feature Card - Compact
const FeatureCard = ({ icon: Icon, title, description, accentColor = '#00d4ff' }) => (
  <div className="group p-5 bg-[#141414] border border-white/10 rounded-xl hover:border-white/20 transition-all">
    <div 
      className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
      style={{ backgroundColor: `${accentColor}20` }}
    >
      <Icon className="h-5 w-5" style={{ color: accentColor }} />
    </div>
    <h3 
      className="font-bold text-white mb-1 uppercase tracking-tight"
      style={{ fontFamily: 'Oswald, sans-serif' }}
    >
      {title}
    </h3>
    <p className="text-sm text-zinc-500">{description}</p>
  </div>
);

// Highlight Clip Card — plays Cloudflare clip inline on click
const HighlightClipCard = ({ clip }) => {
  const dur = clip.duration_seconds
    ? `${Math.floor(clip.duration_seconds / 60)}:${String(Math.round(clip.duration_seconds % 60)).padStart(2, '0')}`
    : null;
  return (
    <Link
      to={`/clip/${clip.clip_id}`}
      className="group relative block aspect-video rounded-xl overflow-hidden bg-[#141414] border border-white/10 hover:border-white/30 transition-all"
      data-testid={`home-highlight-clip-${clip.clip_id}`}
    >
      {clip.thumbnail_url ? (
        <img
          src={clip.thumbnail_url}
          alt={clip.title || 'Highlight clip'}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a0a] to-[#1a1a1a] flex items-center justify-center">
          <Video className="w-12 h-12 text-zinc-700" />
        </div>
      )}
      {/* Dim overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
      {/* Play button */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-14 h-14 rounded-full bg-white/90 backdrop-blur flex items-center justify-center group-hover:scale-110 group-hover:bg-white transition-all">
          <Play className="w-6 h-6 text-black ml-1" fill="black" />
        </div>
      </div>
      {/* Duration badge */}
      {dur && (
        <div className="absolute top-3 right-3 px-2 py-0.5 rounded-md bg-black/80 text-white text-xs font-mono">
          {dur}
        </div>
      )}
      {/* HIGHLIGHT label */}
      <div className="absolute top-3 left-3 px-2 py-0.5 rounded-md bg-[#ff0000] text-white text-[10px] font-bold uppercase tracking-wider">
        Highlight
      </div>
      {/* Title */}
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <h3
          className="text-white font-bold text-base md:text-lg line-clamp-2"
          style={{ fontFamily: 'Oswald, sans-serif' }}
        >
          {clip.title || 'Untitled Highlight'}
        </h3>
      </div>
    </Link>
  );
};

// Newsroom Article Card — preview tile for the home page
const NewsroomCard = ({ article }) => {
  const date = article.published_at
    ? new Date(article.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
  const primaryTag = (article.tags || [])[0];
  return (
    <Link
      to={`/articles/${article.slug}`}
      className="group block bg-[#141414] border border-white/10 rounded-xl overflow-hidden hover:border-white/30 transition-all"
      data-testid={`home-newsroom-${article.slug}`}
    >
      <div className="aspect-[16/9] bg-gradient-to-br from-[#0a0a0a] to-[#1a1a1a] relative overflow-hidden">
        {article.cover_image ? (
          <img
            src={article.cover_image}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center px-6">
              <p
                className="text-[#00d4ff] text-[10px] font-bold uppercase tracking-[0.2em] mb-2"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Newsroom
              </p>
              <h4
                className="text-white text-lg md:text-xl font-black uppercase tracking-tight line-clamp-3"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                {article.title}
              </h4>
            </div>
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2 text-[11px]">
          {primaryTag && (
            <Badge className="bg-[#ff0000]/15 text-[#ff5555] border border-[#ff0000]/30 rounded-md font-bold uppercase tracking-wider text-[10px] px-2 py-0">
              {primaryTag}
            </Badge>
          )}
          {date && <span className="text-zinc-500">{date}</span>}
        </div>
        <h3
          className="text-white font-bold text-base md:text-lg mb-2 line-clamp-2 group-hover:text-[#00d4ff] transition-colors"
          style={{ fontFamily: 'Oswald, sans-serif' }}
        >
          {article.title}
        </h3>
        {article.subtitle && (
          <p className="text-sm text-zinc-400 line-clamp-2">{article.subtitle}</p>
        )}
        <div className="mt-3 inline-flex items-center text-xs text-[#00d4ff] font-semibold uppercase tracking-wider">
          Read story
          <ChevronRight className="w-3 h-3 ml-1" />
        </div>
      </div>
    </Link>
  );
};

const Home = () => {
  const [events, setEvents] = useState([]);
  const [games, setGames] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [clips, setClips] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ events: 0, athletes: 0, teams: 0 });
  const tickerRef = useRef(null);
  
  // Filter out TEST athletes - only show real athletes with real photos
  const realAthletes = athletes.filter(a => {
    const name = (a.name || '').toLowerCase();
    const hasRealPhoto = a.avatar && !a.avatar.includes('dicebear') && !a.avatar.includes('placeholder');
    const isNotTest = !name.startsWith('test') && !name.includes('test_') && !name.includes('test athlete');
    return isNotTest && (hasRealPhoto || a.verified);
  });
  const featuredAthletes = realAthletes.length > 0 ? realAthletes.slice(0, 3) : mockAthletes.slice(0, 3);

  const fetchData = async (isRetry = false) => {
    if (!isRetry) setLoading(true);
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      
      const [eventsRes, gamesRes, athletesRes, clipsRes, articlesRes] = await Promise.all([
        fetch(`${API_URL}/api/events`, { signal: controller.signal }),
        fetch(`${API_URL}/api/browse/games`, { signal: controller.signal }),
        fetch(`${API_URL}/api/athletes?limit=20`, { signal: controller.signal }),
        fetch(`${API_URL}/api/vod/clips?limit=20`, { signal: controller.signal }),
        fetch(`${API_URL}/api/articles?limit=3`, { signal: controller.signal })
      ]);
      clearTimeout(timeoutId);
      
      let eventData = [];
      let athleteData = [];
      
      if (eventsRes.ok) {
        eventData = await eventsRes.json();
        setEvents(eventData);
      }
      if (gamesRes.ok) setGames(await gamesRes.json());
      if (athletesRes.ok) {
        athleteData = await athletesRes.json();
        setAthletes(athleteData);
      }
      if (clipsRes.ok) {
        const clipData = await clipsRes.json();
        // Only real clips: have playback URL and not a TEST title
        const realClips = (clipData || []).filter(c =>
          c.playback_url && !(c.title || '').startsWith('TEST')
        );
        setClips(realClips.slice(0, 2));
      }
      if (articlesRes.ok) {
        const artData = await articlesRes.json();
        const list = Array.isArray(artData) ? artData : (artData.items || artData.articles || []);
        setArticles(list.slice(0, 2));
      }
      
      // Calculate real stats
      const realEvents = eventData.filter(e => !e.name?.toLowerCase().includes('test'));
      const realAthletes = athleteData.filter(a => {
        const name = (a.name || '').toLowerCase();
        return !name.startsWith('test') && !name.includes('test_');
      });
      
      setStats({
        events: realEvents.length,
        athletes: realAthletes.length,
        teams: Math.max(realEvents.length * 2, 10) // Approximate based on events
      });
    } catch (err) {
      console.error('Error fetching data:', err);
      if (!isRetry && (err.name === 'AbortError' || err.name === 'TypeError')) {
        setTimeout(() => fetchData(true), 2000);
        return;
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter events - no test events, no past events, no completed events
  const nowMs = Date.now();
  const filteredEvents = events
    .filter(e => !e.name?.toLowerCase().includes('test'))
    .filter(e => {
      // Hide events that have ended (past their end_date, or past their start date if no end_date)
      const endStr = e.end_date || e.date;
      if (!endStr) return true; // Keep if no date info (be lenient)
      const endMs = new Date(endStr).getTime();
      if (Number.isNaN(endMs)) return true;
      // Add 1 day buffer so events stay visible on their final day
      return endMs + 24 * 60 * 60 * 1000 > nowMs;
    })
    .filter(e => e.status !== 'completed' && e.status !== 'archived' && !e.hidden);
  
  // Prioritize: featured first, then try to get variety of sports
  const sportsShown = new Set();
  const featuredEvents = [];
  
  // First, add featured events
  const featured = filteredEvents.filter(e => e.is_featured).slice(0, 2);
  featured.forEach(e => {
    featuredEvents.push(e);
    sportsShown.add((e.sport || '').toLowerCase().split(',')[0]);
  });
  
  // Then add events from sports not yet shown (prioritize soccer, volleyball)
  const prioritySports = ['soccer', 'volleyball', 'football', 'lacrosse'];
  for (const sport of prioritySports) {
    if (featuredEvents.length >= 5) break;
    const sportEvent = filteredEvents.find(e => 
      !featuredEvents.includes(e) && 
      (e.sport || '').toLowerCase().includes(sport)
    );
    if (sportEvent) {
      featuredEvents.push(sportEvent);
      sportsShown.add(sport);
    }
  }
  
  // Fill remaining slots with other events
  for (const event of filteredEvents) {
    if (featuredEvents.length >= 5) break;
    if (!featuredEvents.includes(event)) {
      featuredEvents.push(event);
    }
  }

  // Get live games for ticker - only show games with proper team names
  const liveGames = games
    .filter(g => {
      const isLive = g.status === 'live' || g.status === 'halftime';
      const hasTeamNames = (g.home_team_name && g.home_team_name !== 'Home' && !g.home_team_name.toLowerCase().includes('test')) ||
                           (g.away_team_name && g.away_team_name !== 'Away' && !g.away_team_name.toLowerCase().includes('test'));
      return isLive && hasTeamNames;
    })
    .slice(0, 10);

  // Scroll ticker
  const scrollTicker = (direction) => {
    if (tickerRef.current) {
      const scrollAmount = direction === 'left' ? -300 : 300;
      tickerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <SeoHead
        title="Live Youth & Amateur Sports Streaming"
        description="Stream live youth and amateur sports on Rooam Sports Network. Real-time stats, box scores, athlete profiles, and full game replays. Built for tournament directors, coaches, parents, and fans."
        path="/"
      />
      {/* Hero Section */}
      <section className="relative h-[85vh] min-h-[600px] w-full overflow-hidden">
        {/* Video Background */}
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        
        {/* Overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/50 to-[#0A0A0A]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/80 via-transparent to-[#0A0A0A]/80" />
        
        {/* Content */}
        <div className="absolute inset-0 flex flex-col justify-center items-center text-center px-4">
          {/* Tagline */}
          <h1 
            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black uppercase tracking-tighter leading-none mb-4"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            <span className="text-white">EVERY</span>{' '}
            <span className="text-[#00d4ff]">MOMENT</span>{' '}
            <span className="text-white">COUNTS</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-zinc-300 max-w-2xl mb-8">
            Live streaming, advanced analytics, and recruitment tools for athletes, 
            coaches, and scouts. The future of sports is here.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 justify-center mb-12">
            <Button 
              size="lg" 
              className="bg-[#00d4ff] hover:bg-[#33ddff] text-black font-bold text-lg px-8 py-6 rounded-full shadow-lg shadow-[#00d4ff]/30" 
              asChild
            >
              <Link to="/events">
                <Play className="h-5 w-5 mr-2" fill="currentColor" />
                Watch Now
              </Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white hover:text-black font-bold text-lg px-8 py-6 rounded-full" 
              asChild
            >
              <Link to="/register">
                Join Now
                <ChevronRight className="h-5 w-5 ml-1" />
              </Link>
            </Button>
          </div>
          
          {/* Stats Bar - Always show brand stats */}
          <div className="flex items-center gap-8 sm:gap-16 bg-black/40 backdrop-blur-md rounded-2xl px-8 sm:px-12 py-6 border border-white/10">
            <div className="text-center">
              <div 
                className="text-3xl sm:text-4xl md:text-5xl font-black text-white"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                MULTI
              </div>
              <div className="text-xs sm:text-sm text-zinc-500 uppercase tracking-widest mt-1">Court Streaming</div>
            </div>
            <div className="w-px h-12 bg-white/20" />
            <div className="text-center">
              <div 
                className="text-3xl sm:text-4xl md:text-5xl font-black text-white"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                HD
              </div>
              <div className="text-xs sm:text-sm text-zinc-500 uppercase tracking-widest mt-1">Quality</div>
            </div>
            <div className="w-px h-12 bg-white/20 hidden sm:block" />
            <div className="hidden sm:block text-center">
              <div 
                className="text-3xl sm:text-4xl md:text-5xl font-black text-[#00d4ff]"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                LIVE
              </div>
              <div className="text-xs sm:text-sm text-zinc-500 uppercase tracking-widest mt-1">Streaming</div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Now Ticker */}
      {liveGames.length > 0 && (
        <section className="py-4 bg-[#0A0A0A] border-y border-white/10">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center gap-4">
              {/* Label */}
              <div className="flex items-center gap-2 shrink-0">
                <Radio className="w-4 h-4 text-[#E5FF00] animate-pulse" />
                <span 
                  className="text-xs font-bold text-[#E5FF00] uppercase tracking-widest"
                  style={{ fontFamily: 'Oswald, sans-serif' }}
                >
                  Live Now
                </span>
              </div>
              
              {/* Scroll buttons */}
              <button 
                onClick={() => scrollTicker('left')}
                className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 transition-colors shrink-0"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              {/* Ticker */}
              <div 
                ref={tickerRef}
                className="flex-1 flex items-center gap-3 overflow-x-auto scrollbar-hide"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {liveGames.map(game => (
                  <LiveTickerItem key={game.game_id || game.id} game={game} />
                ))}
              </div>
              
              <button 
                onClick={() => scrollTicker('right')}
                className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 transition-colors shrink-0"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Featured Events - Only show when events exist */}
      {!loading && featuredEvents.length > 0 && (
      <section className="py-12 md:py-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="flex items-end justify-between mb-8">
            <div>
              <p 
                className="text-[#00d4ff] text-xs font-bold uppercase tracking-[0.2em] mb-2"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Don&apos;t Miss Out
              </p>
              <h2 
                className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                Featured Events
              </h2>
            </div>
            <Button 
              variant="outline" 
              className="border-white/20 text-zinc-300 hover:bg-white/10 hover:text-white rounded-full hidden sm:flex" 
              asChild
            >
              <Link to="/events">
                View All
                <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </Button>
          </div>

          {/* Events Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Hero event - spans 2 columns on large screens */}
            {featuredEvents[0] && (
              <div className="md:col-span-2">
                <FeaturedEventCard event={featuredEvents[0]} isLarge />
              </div>
            )}
            {/* Rest of events */}
            {featuredEvents.slice(1, 4).map(event => (
              <FeaturedEventCard key={event.id} event={event} />
            ))}
          </div>
          
          {/* Mobile View All */}
          <div className="mt-6 text-center sm:hidden">
            <Button variant="outline" className="border-white/20 text-zinc-300 rounded-full" asChild>
              <Link to="/events">View All Events</Link>
            </Button>
          </div>
        </div>
      </section>
      )}

      {/* Latest Highlights — 2 most recent real video clips */}
      {!loading && clips.length > 0 && (
      <section className="py-12 md:py-16 px-4" data-testid="home-highlights-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p
                className="text-[#E5FF00] text-xs font-bold uppercase tracking-[0.2em] mb-2"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Watch Now
              </p>
              <h2
                className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                Latest Highlights
              </h2>
            </div>
            <Button
              variant="outline"
              className="border-white/20 text-zinc-300 hover:bg-white/10 hover:text-white rounded-full hidden sm:flex"
              asChild
            >
              <Link to="/events?active_tab=today">
                Browse Games
                <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {clips.map(clip => (
              <HighlightClipCard key={clip.clip_id} clip={clip} />
            ))}
          </div>
          <div className="mt-6 text-center sm:hidden">
            <Button variant="outline" className="border-white/20 text-zinc-300 rounded-full" asChild>
              <Link to="/events?active_tab=today">Browse Games</Link>
            </Button>
          </div>
        </div>
      </section>
      )}

      {/* From the Newsroom — 2 latest published articles */}
      {!loading && articles.length > 0 && (
      <section className="py-12 md:py-16 px-4" data-testid="home-newsroom-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p
                className="text-[#ff5555] text-xs font-bold uppercase tracking-[0.2em] mb-2"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Stories From The Courtside
              </p>
              <h2
                className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                From The Newsroom
              </h2>
            </div>
            <Button
              variant="outline"
              className="border-white/20 text-zinc-300 hover:bg-white/10 hover:text-white rounded-full hidden sm:flex"
              asChild
            >
              <Link to="/articles">
                View All
                <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {articles.map(article => (
              <NewsroomCard key={article.id || article.slug} article={article} />
            ))}
          </div>
          <div className="mt-6 text-center sm:hidden">
            <Button variant="outline" className="border-white/20 text-zinc-300 rounded-full" asChild>
              <Link to="/articles">Read More Stories</Link>
            </Button>
          </div>
        </div>
      </section>
      )}

      {/* Features Grid */}
      <section className="py-12 md:py-16 px-4 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 
              className="text-2xl md:text-3xl font-black text-white uppercase tracking-tight mb-2"
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              Everything You Need
            </h2>
            <p className="text-zinc-500 max-w-xl mx-auto">
              From live streaming to advanced analytics — tools for athletes, coaches, and scouts
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <FeatureCard icon={Video} title="Live Streaming" description="Watch anywhere" accentColor="#00d4ff" />
            <FeatureCard icon={Sparkles} title="Advanced Stats" description="Smart insights" accentColor="#E5FF00" />
            <FeatureCard icon={TrendingUp} title="Stats Tracking" description="Track progress" accentColor="#30D158" />
            <FeatureCard icon={Award} title="Recruitment" description="Get discovered" accentColor="#00d4ff" />
            <FeatureCard icon={Zap} title="Highlights" description="Instant clips" accentColor="#E5FF00" />
            <FeatureCard icon={Target} title="Game Film" description="Review plays" accentColor="#0A84FF" />
          </div>
        </div>
      </section>

      {/* Featured Athletes - Only show when real athletes exist */}
      {!loading && realAthletes.length > 0 && (
      <section className="py-12 md:py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p 
                className="text-[#00d4ff] text-xs font-bold uppercase tracking-[0.2em] mb-2"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Rising Stars
              </p>
              <h2 
                className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                Featured Athletes
              </h2>
            </div>
            <Button 
              variant="outline" 
              className="border-white/20 text-zinc-300 hover:bg-white/10 hover:text-white rounded-full hidden sm:flex" 
              asChild
            >
              <Link to="/athletes">
                View All
                <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </Button>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {realAthletes.slice(0, 3).map(athlete => (
              <AthleteCard key={athlete.id} athlete={athlete} />
            ))}
          </div>
          
          <div className="mt-6 text-center sm:hidden">
            <Button variant="outline" className="border-white/20 text-zinc-300 rounded-full" asChild>
              <Link to="/athletes">View All Athletes</Link>
            </Button>
          </div>
        </div>
      </section>
      )}

      {/* Partner CTA */}
      <section className="py-12 md:py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#00d4ff] to-[#00a8cc]">
            <div className="relative p-8 md:p-12 lg:p-16">
              <div className="max-w-2xl">
                <p className="text-white/70 text-sm uppercase tracking-widest mb-2">For Organizations</p>
                <h2 
                  className="text-3xl md:text-4xl lg:text-5xl font-black text-white uppercase tracking-tight mb-4"
                  style={{ fontFamily: 'Oswald, sans-serif' }}
                >
                  Stream Your Events on Rooam
                </h2>
                <p className="text-white/80 text-lg mb-6">
                  Schools, leagues, and tournament directors — reach a wider audience and unlock new revenue streams.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button size="lg" className="bg-white text-black hover:bg-zinc-100 font-bold rounded-full" asChild>
                    <Link to="/partner">
                      <Users className="h-5 w-5 mr-2" />
                      Become a Partner
                    </Link>
                  </Button>
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-full" asChild>
                    <Link to="/membership">
                      View Plans
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 px-4 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-black text-white uppercase tracking-tight mb-4"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            Ready to <span className="text-[#00d4ff]">Elevate</span> Your Game?
          </h2>
          <p className="text-zinc-400 text-lg mb-8">
            Join thousands of athletes, coaches, and scouts on Rooam Sports Network
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button size="lg" className="bg-[#00d4ff] hover:bg-[#33ddff] text-white font-bold rounded-full shadow-lg shadow-[#00d4ff]/30" asChild>
              <Link to="/register">Join Now</Link>
            </Button>
            <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-full" asChild>
              <Link to="/events">Explore Events</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
